import { create } from 'zustand'

import type { AgentStyle } from '~/game/agentCharacters'
import type { AgentLook } from '~/game/agentTexture'
import type { EventBus } from '~/realtime/bus'

/**
 * The agents' looks for the panels (the floor keeps its own copy in the
 * scene): the role sets, stored looks and unplugged set, as the last bus
 * events said them. Fed from the bus, so the real app's looks poll and the
 * demo's both reach it the same way the floor hears them.
 */
interface AgentLooksState {
  hr: ReadonlySet<string>
  personal: ReadonlySet<string>
  ea: ReadonlySet<string>
  steward: ReadonlySet<string>
  stewardStaff: ReadonlySet<string>
  styles: Readonly<Record<string, AgentStyle>>
  unplugged: ReadonlySet<string>
}

const EMPTY: AgentLooksState = {
  hr: new Set(),
  personal: new Set(),
  ea: new Set(),
  steward: new Set(),
  stewardStaff: new Set(),
  styles: {},
  unplugged: new Set(),
}

export const useAgentLooks = create<AgentLooksState>(() => EMPTY)

/** Keep the store in step with a bus's looks events (full sets each emit). */
export function followAgentLooks(bus: EventBus): () => void {
  useAgentLooks.setState(EMPTY)
  return bus.on((event) => {
    if (event.kind === 'agentHr') useAgentLooks.setState({ hr: new Set(event.userIds) })
    else if (event.kind === 'agentPersonal') {
      useAgentLooks.setState({ personal: new Set(event.userIds) })
    } else if (event.kind === 'agentEa') useAgentLooks.setState({ ea: new Set(event.userIds) })
    else if (event.kind === 'agentSteward') {
      const steward = new Set(event.userIds)
      useAgentLooks.setState({
        steward,
        stewardStaff: new Set(event.staffUserIds.filter((id) => steward.has(id))),
      })
    } else if (event.kind === 'agentStyle') useAgentLooks.setState({ styles: event.styles })
    else if (event.kind === 'agentUnplugged') {
      useAgentLooks.setState({ unplugged: new Set(event.userIds) })
    }
  })
}

/** One agent's look from the store. */
export function agentLookFor(state: AgentLooksState, userId: string): AgentLook {
  const personal = state.personal.has(userId)
  return {
    hr: state.hr.has(userId),
    personal,
    ea: !personal && state.ea.has(userId),
    steward: state.steward.has(userId),
    stewardStaff: state.stewardStaff.has(userId),
    style: state.styles[userId] ?? null,
    unplugged: state.unplugged.has(userId),
  }
}
